<template>
  <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
    <b-modal
      v-model="isOpen"
      can-cancel
      has-modal-card
      trap-focus
      scroll="keep"
      aria-label="DocumentsModal"
      :aria-modal="true"
      class="DocumentsModal"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <H3TitleView class="modal-card-title">{{ $t("actualitesPage.uploadFile") }} </H3TitleView>
        </header>

        <div class="anneeDiplome">
          <span>{{ $t("DocumentsPage.DocumentModalView.current-year") }} :</span>
          <b-select
            v-model="selectedAnneeScolaire"
            class="selectAnne"
            @update:model-value="goToYear()"
          >
            <option
              v-for="option in anneesScolaires"
              :key="option"
              :value="option"
              :selected="option === selectedAnneeScolaire"
            >
              {{ option }}
            </option>
          </b-select>
        </div>
        <div class="typeDiplome">
          <span>{{ $t("DocumentsPage.DocumentModalView.type-diplome") }} :</span>

          <b-select
            id="typeDocument"
            v-model="typeDocument"
            :placeholder="$t(`StructureSelectionView.placeholder-type`)"
            @update:model-value="handleSelectChange"
          >
            <option />
            <option v-for="option in typesLabels" :key="option" :value="option">
              {{ option }}
            </option>
          </b-select>
        </div>
        <div class="fileinp">
          <input id="inp" type="file" accept=".pdf" @change="uploadFile" />
        </div>
        <footer class="modal-card-foot">
          <div class="buttons">
            <div class="hoverable-discrete-link mr-3" @click="emitCloseModale">
              {{ $t("close-generic") }}
            </div>
            <b-button
              id="btnSubmit"
              type="is-primary"
              disabled="disabled"
              @click="handleSubmit(submit)"
            >
              {{ $t("DocumentsPage.importe") }}
            </b-button>
          </div>
        </footer>
      </div>
    </b-modal>
  </ValidationObserver>
</template>

<script lang="ts">
import {
  DocumentsDTO,
  FilterDTO,
  PaginationOrder,
  PaginationParameter,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { defineComponent, PropType } from "vue"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { Filters } from "@/model/bean/Filters"
import { GraeService } from "@/services/rest/grae/GraeService"
import { t } from "@/utils/i18n-utils"

import { InfoReportingService } from "@/services/log/InfoReportingService"
import { DocumentsService } from "@/services/rest/documents/DocumentsService"
import ConversionUtils from "@/services/ConversionUtils"
import H3TitleView from "@/views/commons/H3TitleView.vue"
import { useTableView } from "@/hooks/useTableView"
import Column from "@/views/tables/Column"
import { BButton, BModal, BSelect } from "buefy"

const TYPE_CHOICES = {
  TYPE1: t("mapDraw.type-choices.type1"),
  TYPE2: t("mapDraw.type-choices.type2"),
  TYPE3: t("mapDraw.type-choices.type3"),
  TYPE4: t("mapDraw.type-choices.type4"),
}

export default defineComponent({
  name: "DocumentsModalView",
  components: {
    H3TitleView,
    ValidationObserver,
    BButton,
    BSelect,
    BModal,
  },
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    open: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    inputNotice: { type: String, default: "" },
    data: {
      type: Array as PropType<DocumentsDTO[]>,
      required: true,
    },
  },

  emits: ["close", "close-refresh"],
  data() {
    return {
      documentsService: DocumentsService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      graeService: GraeService.INSTANCE,
      document: new DocumentsDTO(),
      selectedAnneeScolaire: "",
      typeDocument: "",
      innerValue: undefined as File | undefined,
      loggedUser: new UtilisateurDTO(),
      anneesScolaires: [] as Array<string>,
      typesLabels: TYPE_CHOICES,
      selectedFile: "",
      fileName: "",
      hasDocument: false,
      tableView: null as any,
    }
  },
  computed: {
    isOpen: {
      get(): boolean {
        return this.open
      },
      set(value: boolean): void {
        if (!value) {
          this.$emit("close")
        }
      },
    },
    // Propriétés mappées depuis tableView.state
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },
    currentPagedSortedParameter(): PaginationParameter {
      return this.tableView?.state.currentPagedSortedParameter
    },
    activeFilters(): Array<FilterDTO> {
      return this.tableView?.state.activeFilters || []
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total(): number {
      return this.tableView?.state.total || 0
    },
    config(): any {
      return this.tableView?.state.config
    },
    tableRef(): string {
      return this.tableView?.state.tableRef || "tableDocumentsModalView"
    },
  },
  created() {
    // Initialisation du hook avec binding correct
    this.tableView = useTableView({
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      tableRef: "tableDocumentsModalView",
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
    })
  },
  mounted() {
    this.tableView?.initMounted()
    this.loadAnneesScolaires()
  },
  methods: {
    // Méthodes conservées de configuration
    getInitialColumns(): Array<Column> {
      return new Array<Column>()
    },
    getDefaultSort(): Array<PaginationOrder> {
      return new Array<PaginationOrder>()
    },
    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },
    updateActiveFilters(newActiveFilters: Array<FilterDTO>): void {
      this.tableView?.updateActiveFilters(newActiveFilters)
    },
    async reloadData(): Promise<void> {
      // Cette classe ne semble pas avoir d'implémentation spécifique pour reloadData
    },
    emitCloseModale(): void {
      this.$emit("close")
    },
    goToYear(): void {
      this.clearAnneeScolaireFilter()

      this.activeFilters.push(
        Filters.asFilter({ property: "anneeScolaire", values: [this.selectedAnneeScolaire] }),
      )
    },
    async loadAnneesScolaires(): Promise<void> {
      this.anneesScolaires = await this.graeService.getAnneesScolaires()
      this.selectedAnneeScolaire ||= this.anneesScolaires[0]
      this.goToYear()
    },
    clearAnneeScolaireFilter(): void {
      this.updateActiveFilters(
        this.activeFilters.filter(
          (item: { property: string }) => item.property !== "anneeScolaire",
        ),
      )
    },
    async documentExist(): Promise<void> {
      this.hasDocument = await this.documentsService.getDocumentByTypeAndAnneScolaire(
        this.typeDocument,
        this.selectedAnneeScolaire,
      )
    },
    async submit(): Promise<void> {
      await this.documentExist()
      if (this.hasDocument) {
        this.infoReportingService.error(
          this.$t("DocumentsPage.document-exist").toString(),
          new Error(),
        )
      } else {
        try {
          this.document.type = this.typeDocument
          this.document.anneescolaire = this.selectedAnneeScolaire
          this.document.documentname = this.selectedFile
          this.document.file = this.fileName
          let foundValues = false

          for (let i = 0; i < this.data.length; i++) {
            if (
              this.data[i].anneescolaire === this.selectedAnneeScolaire &&
              this.data[i].type === this.typeDocument
            ) {
              foundValues = true
            }
          }

          if (!foundValues) {
            this.$emit("close-refresh", this.document)
          } else {
            this.reportDocumentModificationError(new Error("Document not found"))
          }
        } catch (e: any) {
          this.reportDocumentModificationError(e as Error)
        }
      }
    },
    reportDocumentModificationError(e: Error): void {
      this.infoReportingService.error(this.$t("DocumentsPage.error-upload").toString(), e)
    },
    handleSelectChange(value: string): void {
      this.typeDocument = value
      const btnSubmit = document.getElementById("btnSubmit") as HTMLButtonElement
      btnSubmit.disabled = !(this.typeDocument != "" && this.selectedFile != "")
    },
    async uploadFile(event: any): Promise<void> {
      const file = event.target.files[0]
      this.fileName = file.name
      if (file) {
        try {
          this.selectedFile = await ConversionUtils.convertFileToBase64String(file)
          const btnSubmit = document.getElementById("btnSubmit") as HTMLButtonElement
          btnSubmit.disabled = !(this.typeDocument != "" && this.selectedFile != "")
        } catch (error) {
          console.error("Error converting file to base64:", error)
        }
      }
    },
  },
})
</script>

<style lang="scss">
.DocumentsModal {
  &.modal .modal-card {
    height: 75%;
    background: white;
    overflow-y: auto !important;
  }

  .animation-content {
    height: 100%;
  }
}

.ActualitesModalView-infos {
  font-style: italic;
}

.typeDiplome {
  padding-top: 15px;
  padding-bottom: 15px;
  background: whitesmoke;
  text-align: center;
}

.anneeDiplome {
  padding-top: 15px;
  padding-bottom: 15px;
  background: white;
  text-align: center;
}

.fileinp {
  width: 100%;
  height: 100%;
  margin: auto;
  text-align: center;
}

#inp {
  text-align: center;
  margin-top: 40px;
}

.uploader {
  display: flex;
  flex-direction: column;
  background-color: #efeeea;
  justify-content: flex-end;
  align-items: center;
  padding: 24px;

  .inputNotice {
    color: #5e5d57;
    margin-bottom: 12px;
  }

  .uploaderInput {
    margin: 0;
  }

  &.disabled {
    pointer-events: none;
  }
}
</style>
